import {
  AlertIcon,
  CrossIcon,
  CurecallCopyButton,
  CurecallTooltip,
  ECurecallSize,
  RoundInfoIcon,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import type { TPatient, TPatientCommunicationChannelStatus } from 'src/types/patient'
import { formatPhone, formatPhoneToInternational, getPhoneAndCaregiver } from 'src/utils/phone.util'
import { twMerge } from 'src/utils/tw-merge.util'
import { useSession } from 'src/providers/auth.provider'
import { AdminOnlyBadge } from '../admin-only-badge.component'

type TCommunicationChannel = {
  type: 'RCS' | 'SMS' | 'TIME2CHAT'
  phone: string
}

function AssignedCommunicationChannel({
  communicationChannel,
  communicationChannelStatus,
  phoneCarrier,
  onOverrideChannel,
}: Readonly<{
  communicationChannel: TCommunicationChannel | undefined
  communicationChannelStatus: TPatientCommunicationChannelStatus | null | undefined
  phoneCarrier: string | undefined
  onOverrideChannel?: () => void
}>) {
  const { t } = useTranslation()
  const { currentUser } = useSession()
  if (!currentUser?.isAdminOrSales) return

  const handleClick = onOverrideChannel
    ? onOverrideChannel
    : () => {
        communicationChannel?.phone && navigator.clipboard.writeText(communicationChannel.phone)
      }

  if (!communicationChannel && communicationChannelStatus === 'NO_CHANNEL_YET')
    return (
      <div className="c-flex c-items-center c-gap-1">
        <CurecallTooltip
          tooltipPosition="bottom"
          content={t('components.patient-phone.communication-channel.pending')}
        >
          <div onClick={onOverrideChannel} className={twMerge(onOverrideChannel && 'c-cursor-pointer')}>
            <RoundInfoIcon className="c-text-grey-500 c-h-4" />
          </div>
        </CurecallTooltip>
        <AdminOnlyBadge />
      </div>
    )
  if (!communicationChannel)
    return (
      <div className="c-flex c-items-center c-gap-1">
        <CurecallTooltip
          tooltipPosition="bottom"
          content={t('components.patient-phone.communication-channel.missing')}
        >
          <div onClick={onOverrideChannel} className={twMerge(onOverrideChannel && 'c-cursor-pointer')}>
            <CrossIcon className="c-text-red-400 c-h-4" />
          </div>
        </CurecallTooltip>
        <AdminOnlyBadge />
      </div>
    )
  if (communicationChannel.type === 'RCS') {
    return (
      <div className="c-flex c-items-center c-gap-1">
        <CurecallTooltip
          tooltipPosition="bottom"
          content={t('components.patient-phone.communication-channel.rcs', {
            carrier: phoneCarrier || 'OperateurInconnu',
          })}
        >
          <div onClick={handleClick} className="c-cursor-pointer">
            <RoundInfoIcon className="c-text-blue-500 c-h-4" />
          </div>
        </CurecallTooltip>
        <AdminOnlyBadge />
      </div>
    )
  }
  if (communicationChannel.type === 'TIME2CHAT' || communicationChannel.type === 'SMS') {
    return (
      <CurecallTooltip
        tooltipPosition="bottom"
        content={
          <span className="c-flex c-items-center c-gap-2">
            <AdminOnlyBadge variant="tooltip" />
            {t('components.patient-phone.communication-channel.sms', {
              phone: formatPhone(communicationChannel.phone),
              carrier: phoneCarrier || 'OperateurInconnu',
            })}
          </span>
        }
      >
        <div onClick={handleClick} className="c-cursor-pointer">
          <RoundInfoIcon className="c-text-black c-h-4" />
        </div>
      </CurecallTooltip>
    )
  }
}

const PatientPhone = ({
  patient,
  showCopyButton,
  showCommunicationChannelIcon,
  onOverrideChannel,
}: {
  patient: Pick<
    TPatient,
    | 'phone'
    | 'phoneCarrier'
    | 'hasCaregiver'
    | 'isPhoneValid'
    | 'CommunicationChannel'
    | 'communicationChannelStatus'
  >
  showCopyButton?: boolean
  showCommunicationChannelIcon?: boolean
  onOverrideChannel?: () => void
}) => {
  const { t } = useTranslation()

  if (!patient.phone) {
    return (
      <div className="c-flex c-items-center c-gap-2 c-min-w-fit c-w-max">
        {
          <CurecallTooltip content={t('components.patient-phone.phone-is-required')} tooltipPosition="bottom">
            <span className="c-text-red-800 c-flex c-items-center c-gap-1">
              {t('components.patient-phone.missing-phone')}
              <AlertIcon color="RED" className="c-h-5" />
            </span>
          </CurecallTooltip>
        }
      </div>
    )
  }

  const isPhoneObuscated = patient.phone?.includes('**')
  if (isPhoneObuscated) {
    return patient.phone
  }

  return (
    <>
      {showCopyButton && patient.phone && (
        <CurecallTooltip
          tooltipPosition="bottom"
          content={`${t('common.actions.copy')} : ${t('common.patient.phone')}`}
        >
          <CurecallCopyButton
            ariaLabelText={t('common.patient.phone')}
            data={formatPhoneToInternational(patient.phone) as string}
            size={ECurecallSize.MEDIUM}
            className="tw-w-max tw-mr-2 tw-h-8"
          />
        </CurecallTooltip>
      )}
      <div className="c-flex c-items-center c-gap-2 c-min-w-fit c-w-max">
        {patient.isPhoneValid ? (
          <span className="c-flex c-items-center c-gap-2">
            {getPhoneAndCaregiver({ t, patient })}
            {showCommunicationChannelIcon && (
              <AssignedCommunicationChannel
                communicationChannel={patient.CommunicationChannel}
                communicationChannelStatus={patient.communicationChannelStatus}
                phoneCarrier={patient.phoneCarrier}
                onOverrideChannel={onOverrideChannel}
              />
            )}
          </span>
        ) : (
          <CurecallTooltip
            content={t('components.patient-phone.phone-number-cant-be-used')}
            tooltipPosition="bottom"
          >
            <span className="c-text-red-800 c-flex c-items-center c-gap-1">
              {getPhoneAndCaregiver({ t, patient })}
              <AlertIcon color="RED" className="c-h-5" />
            </span>
          </CurecallTooltip>
        )}
      </div>
    </>
  )
}

export { PatientPhone }
